import type { Player } from "@hybrid-iptv/player";
import { isFeatureUnlocked } from "../library/proFeatures.js";

/** Fast-forwarding to within this many seconds of live goes all the way. */
const LIVE_SNAP_S = 5;

/**
 * Within this many seconds of live counts as live: no ⏩, no ● LIVE, no time
 * behind. The live point is an estimate between segments, and a control that
 * flickered on and off at live moved under the remote.
 */
export const AT_LIVE_S = 3;

/** "45 s", "2:05", "1:02:05": how far behind live the picture is. */
export function formatBehind(seconds: number): string {
  const total = Math.round(seconds);
  if (total < 60) return `${total} s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = String(total % 60).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${s}` : `${m}:${s}`;
}

/**
 * Rewind or fast-forward live TV inside its timeshift buffer by `seconds`
 * (negative is back), the one rule for the remote's keys and the player bar's
 * buttons and seek bar. Returns what to tell the viewer.
 */
export function shiftLiveBy(engine: Player | null | undefined, seconds: number): string {
  // Pausing and rewinding live TV is Pro (ADR 2026-10-10). Said as a note, never a popup: this runs
  // on the arrow keys while watching full screen, and a dialog there would take the viewer's focus.
  if (!isFeatureUnlocked("timeshift")) return "Rewinding live TV is part of Pro";
  const shift = engine?.timeshift();
  // Nothing saved at all: timeshift is off, or this channel cannot use it.
  if (!engine || !shift || shift.behindSeconds + shift.offsetSeconds < 1) {
    return seconds < 0 ? "Live TV can't go back yet" : "Already at live";
  }
  if (seconds > 0 && shift.offsetSeconds < AT_LIVE_S) return "Already at live";
  if (seconds < 0 && shift.behindSeconds < 1) return `⏪ As far back as it goes: ${formatBehind(shift.offsetSeconds)} behind live`;
  let offset = Math.min(0, Math.max(-(shift.behindSeconds + shift.offsetSeconds), seconds - shift.offsetSeconds));
  // A few seconds short of live is live: no one wants to be told "2 s behind".
  if (seconds > 0 && offset > -LIVE_SNAP_S) offset = 0;
  engine.seekFromLiveEdge(offset);
  return offset === 0 ? "● Live" : `${seconds < 0 ? "⏪" : "⏩"} ${formatBehind(-offset)} behind live`;
}
